export type TenantMetadata = {
  name?: string | null;
  title?: string | null;
  description?: string | null;
  faviconUrl?: string | null;
  logoUrl?: string | null;
};

const DEFAULT_TITLE = 'PathIQ';
const DEFAULT_DESCRIPTION = 'Create, launch, and manage campaigns with PathIQ.';
/**
 * A 1x1 transparent icon, not a logo. Branding here is per-tenant, so when a
 * tenant has not uploaded a favicon the tab stays blank rather than showing
 * another brand's mark. It is a real file rather than an absent <link> because
 * browsers fall back to requesting /favicon.ico on their own when no icon is
 * declared.
 */
const BLANK_FAVICON = '/favicon.ico';

const setMetaTag = (name: string, content: string) => {
  if (typeof document === 'undefined') return;

  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }

  meta.content = content;
};

const resolveUrl = (href: string): string => {
  try {
    return new URL(href, document.baseURI).href;
  } catch {
    return href;
  }
};

/**
 * Replaces the tag rather than mutating its href. Several browsers ignore an
 * href change on an icon <link> that is already in the document and keep
 * painting the icon they first fetched, which is exactly the case here: the
 * blank default is in the markup and the tenant's icon arrives afterwards.
 */
const setLinkTag = (rel: string, href: string) => {
  if (typeof document === 'undefined') return;

  const existing = document.querySelectorAll<HTMLLinkElement>(
    `link[rel="${rel}"]`
  );
  // `link.href` reads back absolute, so resolve before comparing — otherwise
  // the relative blank default never matches and every call refetches it.
  const resolved = resolveUrl(href);
  if (existing.length === 1 && existing[0].href === resolved) return;

  existing.forEach((link) => link.remove());

  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  document.head.appendChild(link);
};

export const applyTenantMetadata = (metadata: TenantMetadata) => {
  if (typeof document === 'undefined') return;

  const name = metadata.name || DEFAULT_TITLE;
  const title = metadata.title || name;
  const description = metadata.description || DEFAULT_DESCRIPTION;
  const faviconUrl = metadata.faviconUrl || BLANK_FAVICON;

  document.title = title;
  setMetaTag('description', description);
  setLinkTag('icon', faviconUrl);
  setLinkTag('apple-touch-icon', faviconUrl);
};
